// AppShellHost — the singleton-tab and history intents (hotspot split out of
// useAppShellHost). Each is one dispatch of a named FrameTransition; every
// close-* (and a new conversation) forwards a freshly minted conversation id,
// the never-zero seed. A plain factory: it calls no React hooks, so the call
// site adds nothing to the hook order.

import type { Dispatch } from "react";
import type { ConversationId } from "../../components/Navigation/types.js";
import type { SearchFacet } from "../../components/Search/searchModel.js";
import type { FrameTransition } from "../frame.js";

export const shellNavIntents = (
    dispatch: Dispatch<FrameTransition>,
    mkConversationId: () => ConversationId,
    // Whether the chat's conversation is empty: nascent, nothing sent.
    chatIsEmpty: () => boolean,
) => ({
    // Walk the window's ONE focus-history stack (nav-audit Fix 1): the same
    // intents the ⌘[ / ⌘] chords + the TopStrip Back/Forward buttons fire.
    navBack: () => dispatch({ type: "nav-back" }),
    navForward: () => dispatch({ type: "nav-forward" }),
    // Settings + Home singleton tabs: each rail/menu pick its own intent.
    gotoSettings: () => dispatch({ type: "open-settings", section: "account" }),
    closeSettings: () =>
        dispatch({
            type: "close-settings",
            freshConversationId: mkConversationId(),
        }),
    gotoHome: () => dispatch({ type: "open-home" }),
    closeHome: () =>
        dispatch({
            type: "close-home",
            freshConversationId: mkConversationId(),
        }),
    // The Studio canvas singleton tab (Studio v0): the sidebar's Studio row.
    gotoStudio: () => dispatch({ type: "open-studio" }),
    // Uploaded sources: Search scoped to Sources (pen "One search").
    gotoKnowledge: () => dispatch({ type: "open-search", kind: "source" }),
    // Search singleton tab; `kind` optionally pre-selects a scope.
    gotoSearch: (kind?: SearchFacet) => dispatch({ type: "open-search", kind }),
    closeSearch: () =>
        dispatch({
            type: "close-search",
            freshConversationId: mkConversationId(),
        }),
    // Start a fresh conversation: the SAME `new-tab` transition the strip's "+"
    // fires; shared by every New (the rail icon, the omnibar's "New
    // conversation" row, the chat heads). Idempotent while the chat's
    // conversation is empty: the Author is already in a new one, so there is
    // never a second empty one (#431).
    startNewConversation: () => {
        if (!chatIsEmpty())
            dispatch({ type: "new-tab", conversationId: mkConversationId() });
    },
});
